// @ts-check
import { gql } from '@apollo/client/core';
import React from 'react';
import {
  Box,
  Flex,
  Page,
  Product,
  Text,
  Title,
  Wine,
} from '../../pdf-design-system';
import { Image } from '@react-pdf/renderer';
import { PagesProvider } from '../../pdf-design-system/Base/PageLayout/Page';
import sourceSansPro400 from '../../pdf-design-system/fonts/source-sans-pro/400';
import sourceSansPro600 from '../../pdf-design-system/fonts/source-sans-pro/600';
import openSans400 from '../../pdf-design-system/fonts/open-sans/300';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
//import eventData from './data';
import gothamBold from './fonts/gothamBold';
import messages from './locale/messages';

const productsPerPage = 8;
export default function TastingBooklet({ data, locale, ...rest }) {
  // const data = { ...baseData, siteData: eventData };
  // const baseProducts = mapEventData(data);
  // const products = [...baseProducts, ...baseProducts];
  const products = mapEventData(data);

  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {Array.from(Array(Math.ceil(products.length / productsPerPage))).map(
          (_, index) => (
            <TastingBookletPage
              products={products.slice(
                index * productsPerPage,
                index * productsPerPage + productsPerPage
              )}
              theme={theme}
              collection={data.collection}
              {...rest}
            />
          )
        )}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

function mapEventData(data) {
  const {
    siteData: { exhibitors, products, tastingNotes, bookmarks },
  } = data;

  const productIds = bookmarks?.exhibitors?.reduce((acc, exhibitor) => {
    const productIds = exhibitor.products.map((product) => product.id);
    acc = [...acc, ...productIds];
    return acc;
  }, []);

  const productsToBeShown = products.all.filter((product) => {
    return productIds.includes(product.productId);
  });
  return productsToBeShown;
}

function TastingBookletPage({
  products,
  theme,
  collection,
  bleed = 0,
  style,
  baseUrl,
  ...rest
}) {
  const productsPerA5 = productsPerPage / 2;
  const page1Products = products?.slice(0, productsPerA5);
  const page2Products = products?.slice(productsPerA5, 2 * productsPerA5);

  return (
    <Page
      size="A4"
      style={[
        { paddingVertical: `${bleed}mm`, paddingHorizontal: `${bleed}mm` },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      <A5PageUnit products={page1Products} collection={collection} />
      <Box
        style={{
          borderBottom: 1,
          borderColor: '#ccc',
        }}
      />
      {page2Products?.length > 0 && (
        <A5PageUnit products={page2Products} collection={collection} />
      )}
    </Page>
  );
}

function A5PageUnit({ products, collection }) {
  console.log('collection', collection);
  return (
    <Box
      style={{
        paddingHorizontal: '10mm',
        paddingVertical: '10mm',
        height: '148mm',
        position: 'relative',
      }}
    >
      <Flex style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Title variant="large" style={{ marginBottom: '2mm' }}>
          My Tasting Booklet
        </Title>
        <Image src={collection.logo?.fixed?.src} style={{ height: 20 }} />
      </Flex>
      <Flex direction="column">
        {products?.map((product, index) => {
          const analyticsString = [
            product.designation,
            product.alcohol && `Alc. ${product.alcohol}%`,
            product.residualSugar && `TS ${product.residualSugar} g/l`,
            product.totalAcidity && `GS ${product.totalAcidity} g/l`,
          ]
            .filter(Boolean)
            .join(' | ');
          return (
            <Flex
              key={index}
              style={{
                borderTop: 1,
                borderColor: theme.colors.primary,
                height: '28mm',
                flexShrink: 0,
                paddingVertical: '2mm',
              }}
            >
              <Product.BottleImage height={65} width={30} product={product} />
              <Box style={{ marginLeft: '3mm' }}>
                <Text>
                  {[product.exhibitor.name, product.exhibitor?.stand?.name]
                    .filter(Boolean)
                    .join(' | ')}
                </Text>
                <Text variant="bold">
                  {product.shortName} {product.vintage}
                </Text>
                <Text>{product.denomination}</Text>
                <Wine.Blend.Value product={product} />
                <Text>{analyticsString}</Text>
              </Box>
            </Flex>
          );
        })}
      </Flex>
      <Flex style={{ paddingTop: 20, color: '#888' }}>
        <Text>{collection.name}</Text>
      </Flex>
    </Box>
  );
}
/**
 * @param {ReturnType<afterQuery>[number]} section
 */
function getTable(section) {
  if (section.__typename === 'RegistrationCatalogSection') {
    return section.registration;
  }
  return undefined;
}

/** @typedef {ReturnType<getTable>} Table */

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

function colors(themeId) {
  const colors = {
    finest100: {
      primary: '#ccc',
      accent: '#c41b79',
    },
    champagne: { primary: '#ccc', accent: 'black' },
  };
  return colors[themeId];
}

/** @type {Theme} */
export const theme = {
  colors: colors('champagne'),
  components: {
    Text: {
      base: { ...sourceSansPro400 },
      default: { fontSize: 10 },
      smallest: { fontSize: 6, lineHeight: 1 },
      small: { fontSize: 8, lineHeight: 1.2 },
      boldAccent: {
        ...gothamBold,
        color: colors('champagne').accent,
        fontSize: 10,
      },
      bold: { ...sourceSansPro600, fontSize: 12 },
    },
    Title: {
      base: { ...openSans400 },
      small: {
        fontSize: 8,
        lineHeight: 1,
      },
      default: {
        fontSize: 10,
        lineHeight: 1,
      },
      large: {
        fontSize: 16,
        lineHeight: 1.3,
      },
    },
  },
};

TastingBooklet.fragment = gql`
  fragment TastingBooklet on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      name
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
      logo {
        # 55mm in 300dpi
        fixed(width: 654, crop: PAD, gravity: NORTH, fetch_format: JPG) {
          src
        }
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...Finest100_Table
    }
  }

  fragment Finest100_Table on RegistrationCatalogSection {
    registration {
      registrationId
      name
      companyId
      stand {
        name
      }
      profile {
        ...Finest_Profile
      }

      registeredProducts {
        nodes {
          ...Finest_RegisteredProduct
        }
        byProducer: group(field: producer___name) {
          fieldValue
          nodes {
            # We need to get the profile for the producer for premium registrations
            producer {
              profile {
                ...Finest_Profile
              }
            }
            ...Finest_RegisteredProduct
          }
        }
      }
    }
  }

  fragment Finest_Profile on Profile {
    name
    street
    postalCode
    city
    country
    telephone
    email
    website
    shortDescription
    autoSizedLogo: logo {
      # 55mm in 300dpi
      fixed(width: 654, crop: PAD, gravity: NORTH, fetch_format: JPG) {
        src
      }
    }
    autoSizedMainImage: mainImage {
      # 55mm in 300dpi
      fixed(
        width: 1105
        height: 891
        crop: FILL
        gravity: FACE_AUTO
        fetch_format: JPG
      ) {
        src
      }
    }
  }

  fragment Finest_RegisteredProduct on RegisteredProduct {
    newToMarket: customFieldValue(
      key: "isThisWineNewNewDiscoveryOrNewToMarket"
    ) {
      __typename
      ... on YesNoFieldValue {
        value
      }
    }
    producer {
      isSeekingDistribution
      distributionContacts {
        contactId
        companyName
      }
    }
    productId
    product {
      productId
      name
      alcohol

      regionHierarchy
      denomination
      ...Blend
      ...BottleImage
      ... on Wine {
        designation
        residualSugar
        totalAcidity
      }
    }
  }
  ${Product.Blend.fragment}
  ${Product.BottleImage.fragment}
`;

function afterQuery(data, rest) {
  console.log('HEREEREERRE');
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}
